"use client";

import { useEffect, useState } from "react";

import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { useAuth } from "@/features/auth";

import { useProjectSequences } from "../hooks/use-project-sequences";
import type { SequenceListItem } from "../hooks/use-sequences";

const PER_PAGE = 15;

type SequenceInputProps = {
  value: string;
  onChange: (sequence: SequenceListItem) => void;
} & Omit<
  React.ComponentProps<typeof Select.Trigger>,
  "value" | "onChange" | "children"
>;

export function SequenceInput({
  value,
  onChange,
  className,
  variant,
  ...triggerProps
}: SequenceInputProps) {
  const { selectedProjectId } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const [pickedSequence, setPickedSequence] = useState<SequenceListItem | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      setSearch("");
      setCursor(undefined);
      setCursorStack([]);
    }
  }, [open]);

  useEffect(() => {
    setCursor(undefined);
    setCursorStack([]);
  }, [search]);

  const { sequences, nextCursor, isLoading } = useProjectSequences({
    projectId: selectedProjectId,
    params: {
      per_page: PER_PAGE,
      cursor,
      search: search.trim() || undefined,
    },
  });

  const label = pickedSequence?.id === value ? pickedSequence.name : null;

  const handleNext = () => {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, cursor]);
    setCursor(nextCursor);
  };

  const handlePrev = () => {
    setCursorStack((s) => {
      const popped = s[s.length - 1];
      setCursor(popped);
      return s.slice(0, -1);
    });
  };

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger
        variant={variant}
        className={className}
        active={!!value}
        {...triggerProps}
      >
        <Icon name="IconPaperPlane" size={14} className="shrink-0" />
        <Select.TriggerContent>
          {label || "Select Sequence"}
        </Select.TriggerContent>
        <Select.Icon className="ml-auto" />
      </Select.Trigger>

      <Select.Content
        matchTriggerWidth={false}
        className="w-[380px] min-w-[320px]"
        align="start"
      >
        <Select.Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sequences…"
          autoFocus
        />
        <Select.List>
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Icon
                name="IconLoadingCircle"
                size={18}
                className="animate-spin text-contrast/40"
              />
            </div>
          ) : sequences.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">
                No sequences found
              </p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                {search.trim()
                  ? "No matching sequences. Try a different search."
                  : "No sequences in this project."}
              </p>
            </div>
          ) : (
            <>
              {sequences.map((seq, i) => (
                <Select.Item
                  key={seq.id}
                  index={i}
                  selected={seq.id === value}
                  onSelect={() => {
                    setPickedSequence(seq);
                    onChange(seq);
                    setOpen(false);
                  }}
                >
                  <Select.ItemIcon>
                    <Icon
                      name="IconPaperPlane"
                      size={14}
                      className="text-heading opacity-60"
                    />
                  </Select.ItemIcon>
                  <Select.ItemLabel selected={seq.id === value}>
                    {seq.name || "Unnamed"}
                  </Select.ItemLabel>
                  {seq.id === value && <Select.ItemCheck />}
                </Select.Item>
              ))}
              {(cursorStack.length > 0 || nextCursor) && (
                <div className="flex items-center justify-between border-t border-border px-3 py-2">
                  <button
                    type="button"
                    disabled={cursorStack.length === 0}
                    onClick={handlePrev}
                    className="cursor-pointer text-xs text-contrast/50 transition-colors hover:text-contrast/80 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!nextCursor}
                    onClick={handleNext}
                    className="cursor-pointer text-xs text-contrast/50 transition-colors hover:text-contrast/80 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
